/**
 * Spoof-in-effect auditor advisory (#254 / 06-timeline.md §13.5).
 *
 * Rule: `cursor.spoof.active`
 *
 * Fires `info` when one or more Characters have an ACTIVE `cursor_spoof`
 * belief (i.e., they believe "now" is a different in-world date than the
 * true session cursor). The advisory tells the GM:
 *
 *   "N fact(s) authored under spoof — review for consistency"
 *
 * The GM can see which characters are spoofed (`entity_ids`) and how many
 * facts were authored in the spoof window (`facts_authored_during_spoof`),
 * so they can spot if authored facts accidentally reveal the real date.
 *
 * Context binding (`ctx.cursor_spoof_ops`):
 *
 * - `active_spoofs` — resolved `ActiveSpoofBelief` records (one per
 *   Character with an open spoof); provided by the caller from
 *   `collectActiveSpoofBeliefs`. Absent / empty → rule is a no-op.
 * - `facts_authored_during_spoof` — the caller counts how many fact-create /
 *   fact-edit ops were written at a session cursor that falls within ANY
 *   spoof's active window (`started_at_day ≤ session cursor day < today`).
 *   The engine stays pure (no raw op access); the caller aggregates and
 *   passes this count. Zero is valid (spoof just started).
 *
 * Progressive disclosure: `cursor_spoof_ops` absent or empty → no-op.
 * Severity: `info` baseline; GM can mute via override `'cursor.spoof.active': 'off'`.
 * English-free: `message_key` + `params` only.
 */

import type { RawAdvisory } from '../advisory.js';
import type { AuditContext } from '../engine.js';

/** Rule id for the spoof-in-effect advisory. */
export const CURSOR_SPOOF_ACTIVE_RULE = 'cursor.spoof.active';

/**
 * Emit `info` advisory when at least one Character has an active cursor-spoof
 * belief. One advisory per audit run (not per character — the GM sees a
 * single grouped notice with count + per-character entity references).
 */
export function cursorSpoofRule(ctx: AuditContext): RawAdvisory[] {
  const binding = ctx.cursor_spoof_ops;
  if (!binding || binding.active_spoofs.length === 0) return [];

  const entity_ids = binding.active_spoofs.map((s) => s.character_id).sort();

  return [
    {
      severity: 'info',
      rule: CURSOR_SPOOF_ACTIVE_RULE,
      message_key: 'auditor.cursor_spoof.active',
      params: {
        spoof_count: binding.active_spoofs.length,
        facts_authored_during_spoof: binding.facts_authored_during_spoof,
      },
      fact_ids: [],
      entity_ids,
    },
  ];
}
